// Resolving-tab bridge — the single source of truth for the dynamic "Resolving"
// canvas tab across the Module Federation boundary.
//
// WHY THIS EXISTS: shellStore.ts (Zustand) can be evaluated as separate module
// instances in the host main bundle vs. the CanvasEngine expose bundle, so the
// resolving-tab state written by ESA components is not reliably visible to
// CapabilityTabsLayout. Two primitives ARE true singletons across all chunks:
//   1. window.__RESOLVING_TAB__ — survives late mounts (canvas opened after the
//      session started) so a component can read the spec on mount.
//   2. shellEventBus (window.__SHELL_EVENT_BUS__) — pushes live updates.
//
// Every enter / update / clear / exit MUST go through this module so the window
// snapshot and the event stream can never drift apart.

import { shellEventBus } from './eventBus';
import type { ResolvingTabSpec } from '../../features/Esa/types';
import { RESOLVING_TAB_WINDOW_KEY } from '../../constants/storageKeys';

export const RESOLVING_TAB_SET = 'RESOLVING_TAB_SET';
export const RESOLVING_TAB_CLEAR = 'RESOLVING_TAB_CLEAR';
export const RESOLVING_TAB_EXIT = 'RESOLVING_TAB_EXIT';

const WINDOW_KEY = RESOLVING_TAB_WINDOW_KEY;

function windowBag(): Record<string, unknown> | null {
  return typeof window !== 'undefined'
    ? (window as unknown as Record<string, unknown>)
    : null;
}

/** A spec must carry the identifiers the tab needs to render and route. */
export function isValidResolvingTabSpec(value: unknown): value is ResolvingTabSpec {
  if (typeof value !== 'object' || value === null) return false;
  const spec = value as Partial<ResolvingTabSpec>;
  return (
    typeof spec.title === 'string' &&
    spec.title.length > 0 &&
    typeof spec.hilId === 'string' &&
    spec.hilId.length > 0
  );
}

/** Read the current spec (used by late-mounting components on first render). */
export function getResolvingTab(): ResolvingTabSpec | null {
  const bag = windowBag();
  const value = bag?.[WINDOW_KEY];
  return isValidResolvingTabSpec(value) ? value : null;
}

/** Enter or update the resolving tab: persist to window + broadcast. */
export function setResolvingTab(tab: ResolvingTabSpec): void {
  const bag = windowBag();
  if (bag) bag[WINDOW_KEY] = tab;
  shellEventBus.dispatch({ type: RESOLVING_TAB_SET as never, payload: { tab } as never });
}

/** Clear the resolving tab: wipe window snapshot + broadcast. */
export function clearResolvingTab(): void {
  const bag = windowBag();
  if (bag) bag[WINDOW_KEY] = null;
  shellEventBus.dispatch({ type: RESOLVING_TAB_CLEAR as never, payload: {} as never });
}

/** Request the resolving session to exit (e.g. the canvas tab's × button). */
export function requestResolvingTabExit(): void {
  shellEventBus.dispatch({ type: RESOLVING_TAB_EXIT as never, payload: {} as never });
}
